import { useState, useEffect } from 'react';

/**
 * Custom hook to detect and monitor internet connection status
 * 
 * @returns {Object} Network status information
 * @returns {boolean} isOnline - Current online status
 * @returns {boolean} wasOffline - Whether the connection was previously offline
 */
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      console.log('[useNetworkStatus] Network restored');
      setIsOnline(true);
      
      // Dispatch custom event for components that need to react to network restoration
      if (wasOffline) {
        window.dispatchEvent(new CustomEvent('network:restored'));
      }
      
      setWasOffline(false);
    };

    const handleOffline = () => {
      console.log('[useNetworkStatus] Network lost');
      setIsOnline(false);
      setWasOffline(true);
      
      // Dispatch custom event for components that need to react to network loss
      window.dispatchEvent(new CustomEvent('network:lost'));
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  return { isOnline, wasOffline };
};
